import { map, uniq } from 'es-toolkit/compat';

import { FileToDownload } from 'components/atoms/DownloadFilesModal';
import { ResultsMode } from 'interfaces/experimentResults';
import { fetchFeaturesCsvBlob } from 'utils/exports';
import { downloadFilesAsZip, ZipDownloadError } from 'utils/zipDownload';

type FeaturesCsvOptions = Parameters<typeof fetchFeaturesCsvBlob>[0];

// One Features CSV per post-processing run, in one ZIP. The runs are never merged: each CSV is the existing
// `features` response for that run alone. The ZIP is all or nothing, and a failure names the runs that failed.
export const exportFeaturesForRuns = async ({
  orchestrationIds,
  ...options
}: Omit<FeaturesCsvOptions, 'orchestrationId' | 'resultsMode'> & { orchestrationIds: string[] }) => {
  const { studyId, highlightedOnly, signal } = options;
  const suffix = highlightedOnly ? '_highlighted' : '';
  const files: FileToDownload[] = map(orchestrationIds, (orchestrationId) => ({
    id: orchestrationId,
    // The bytes come from `prepare`; the URL only identifies the entry.
    url: `features://${studyId}/${orchestrationId}${suffix}`,
    title: `run ${orchestrationId}`,
    fileName: `features_study_${studyId}_run_${orchestrationId}${suffix}.csv`,
    prepare: (prepareSignal: AbortSignal) =>
      fetchFeaturesCsvBlob({
        ...options,
        // The form's context names the run selected there; cluster labels are resolved per run.
        context: { ...options.context, orchestrationId },
        resultsMode: ResultsMode.Manual,
        orchestrationId,
        signal: prepareSignal,
      }),
  }));
  try {
    await downloadFilesAsZip({
      files,
      archiveName: `features_study_${studyId}_runs${suffix}.zip`,
      signal: signal ?? new AbortController().signal,
    });
  } catch (error) {
    if (error instanceof ZipDownloadError && error.failedFileNames.length) {
      const reasons = uniq(error.failureReasons);
      throw new Error(
        `Features could not be retrieved for ${error.failedFileNames.join(', ')}.${
          reasons.length ? ` ${reasons.join(' ')}` : ''
        }`
      );
    }
    throw error;
  }
};
